import { Card } from '@/components/ui/card';
import { formatMinor } from '@/lib/money';
import type { FinanceAccount, FinancePairBalance } from '@/api/generated/types.gen';

export interface BalancesGridProps {
  accounts: readonly FinanceAccount[];
  balances: readonly FinancePairBalance[];
}

/**
 * A card per account, a row per currency. `estimated` is printed, not hidden:
 * the pair has no baseline on or before the date, so the figure is summed
 * from payments alone (§5.2) and is a guess about where the account started.
 */
export function BalancesGrid({ accounts, balances }: BalancesGridProps) {
  if (balances.length === 0) {
    return (
      <p className="text-sm text-muted">
        No balances on this date — no account has a baseline or a payment yet.
      </p>
    );
  }
  // The cards follow the GRID, not the account list, so an account with no
  // row simply has no card. Two different things produce a missing row and
  // neither should render as a zero balance: the account is inactive (the
  // server's grid lists active accounts only), or the pair has neither a
  // baseline nor a payment on that date.
  const byAccount = new Map<string, FinancePairBalance[]>();
  for (const b of balances) {
    const list = byAccount.get(b.account_id) ?? [];
    list.push(b);
    byAccount.set(b.account_id, list);
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {[...byAccount.entries()].map(([accountId, rows]) => (
        <Card key={accountId} className="p-4">
          <h3 className="text-sm font-semibold text-[var(--text-heading)]">
            {accounts.find((a) => a.id === accountId)?.name ?? accountId}
          </h3>
          <dl className="mt-2 flex flex-col gap-1">
            {rows.map((r) => (
              <div key={r.currency} className="flex items-baseline justify-between gap-2">
                <dt className="text-xs uppercase tracking-[0.06em] text-muted">{r.currency}</dt>
                <dd className="flex items-center gap-1.5 tabular-nums">
                  {formatMinor(r.balance_minor, r.currency)}
                  {r.estimated && (
                    <span
                      title="No baseline on or before this date — summed from payments alone"
                      className="rounded-full bg-[var(--ollio-gold-light)] px-1.5 py-0.5 text-[0.6875rem] font-semibold text-[var(--ollio-gold-deep)]"
                    >
                      estimated
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {/* Whose date this is, from the answer rather than from the input:
              with no as_of chosen the server used business today. */}
          <p className="mt-2 text-xs text-muted">as of {rows[0]?.as_of}</p>
        </Card>
      ))}
    </div>
  );
}
